import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Typography, Box, Divider, Chip, useTheme, alpha
} from '@mui/material';
import { supabase } from '../../../../supabaseClient';

const StabilityStep = forwardRef(({ currentStep, formulation }, ref) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    study_purpose: '',
    study_id: '',
    study_coordinator: '',
    study_duration: '',
    primary_condition: '',
    humidity_control: '',
    light_exposure: '',
    stress_conditions: '',
    testing_frequency: '',
    custom_timepoints: '',
    samples_per_timepoint: '',
    pull_extra_samples: false,
    package_configuration: '',
    package_details: '',
    package_orientation: '',
    headspace_analysis: false,
    appearance_check: false,
    odor_check: false,
    ph_monitoring: false,
    viscosity_testing: false,
    microbial_testing: false,
    active_ingredient_assay: false,
    preservative_effectiveness: false,
    package_integrity: false,
    trend_analysis_method: '',
    regulatory_standard: '',
    failure_response_plan: '',
    report_distribution: '',
    real_time_study: false,
    accelerated_study: false
  });

  // Load existing data when component mounts or currentStep changes
  useEffect(() => {
    if (currentStep?.stability_details) {
      setFormData(prevData => ({
        ...prevData,
        ...currentStep.stability_details
      }));
    }
  }, [currentStep?.id]);

  // Expose saveData function to parent
  useImperativeHandle(ref, () => ({
    saveData: async () => {
      if (!currentStep?.id) return false;

      try {
        const { error } = await supabase
          .from('procedure_steps')
          .update({
            stability_details: formData,
            updated_at: new Date()
          })
          .eq('id', currentStep.id);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Error saving stability data:', error);
        return false;
      }
    },
    formData
  }), [formData, currentStep?.id]);

  // Handle field changes - only update local state
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box sx={{ 
      p: 4,
      background: `linear-gradient(135deg, ${alpha('#f8f9fa', 0.8)}, ${alpha('#e9ecef', 0.6)})`,
      backdropFilter: 'blur(10px)',
      borderRadius: 4,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }}>
      <Box sx={{ 
        mb: 4,
        p: 4,
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 500, 
          color: theme.palette.primary.main, 
          mb: 1,
          textAlign: 'center'
        }}>
          Stability Study Specifications
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ 
          textAlign: 'center',
          fontWeight: 500
        }}>
          Define comprehensive stability testing protocols and monitoring parameters
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Study Purpose Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.info.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.info.main, 
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              Study Purpose - What are you trying to learn?
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="What type of stability study?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.study_purpose || ''}
                  onChange={(e) => handleFieldChange('study_purpose', e.target.value)}
                  placeholder="Shelf life to determine how long product stays good; Formulation development for testing new formula; Package testing for different containers; Accelerated aging for fast simulation"
                  size="small"
                  helperText="Describe the main purpose of your stability study"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Study ID/Protocol Number"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.study_id || ''}
                  onChange={(e) => handleFieldChange('study_id', e.target.value)}
                  placeholder="STAB-2024-001, Protocol-ABC-123"
                  size="small"
                  helperText="Unique identifier for tracking"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Who is responsible for this study?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.study_coordinator || ''}
                  onChange={(e) => handleFieldChange('study_coordinator', e.target.value)}
                  placeholder="Stability Manager, R&D Scientist"
                  size="small"
                  helperText="Person overseeing the study"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expected study duration"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.study_duration || ''}
                  onChange={(e) => handleFieldChange('study_duration', e.target.value)}
                  placeholder="12 months, 24 months, 36 months"
                  size="small"
                  helperText="How long to run the study"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.info.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.info.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Test Conditions Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.warning.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.warning.main, 
              mb: 3
            }}>
              Test Conditions - Where and how to store samples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Primary storage condition"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.primary_condition || ''}
                  onChange={(e) => handleFieldChange('primary_condition', e.target.value)}
                  placeholder="Room temperature (25°C) for normal storage; Refrigerated (5°C) for cold storage; Frozen (-20°C) for freeze storage; Accelerated (40°C, 75% RH) for fast aging"
                  size="small"
                  helperText="Main storage conditions for testing"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Humidity control needed?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.humidity_control || ''}
                  onChange={(e) => handleFieldChange('humidity_control', e.target.value)}
                  placeholder="Not controlled for ambient humidity; 60% RH ± 5% for standard condition; 75% RH ± 5% for high humidity stress; Dry conditions for low humidity"
                  size="small"
                  helperText="Humidity requirements for storage"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Light exposure"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.light_exposure || ''}
                  onChange={(e) => handleFieldChange('light_exposure', e.target.value)}
                  placeholder="Protected from light for dark storage; Normal lab lighting with fluorescent; Direct sunlight for window exposure; UV light chamber for controlled UV; Photostability chamber for ICH conditions"
                  size="small"
                  helperText="Light exposure conditions during storage"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Additional stress conditions?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.stress_conditions || ''}
                  onChange={(e) => handleFieldChange('stress_conditions', e.target.value)}
                  placeholder="None for normal conditions only; Freeze-thaw cycling; Vibration/agitation stress; High temperature (50°C+); pH extremes (acid/base)"
                  size="small"
                  helperText="Extra stress testing conditions"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.warning.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Testing Schedule Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Testing Schedule - When to test samples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Testing frequency"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.testing_frequency || ''}
                  onChange={(e) => handleFieldChange('testing_frequency', e.target.value)}
                  placeholder="T0, 1M, 3M, 6M for quick study; T0, 1M, 3M, 6M, 12M for standard; T0, 3M, 6M, 12M, 18M, 24M for long term; Custom schedule to define your own"
                  size="small"
                  helperText="When to test samples during the study"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom testing times (if selected)"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.custom_timepoints || ''}
                  onChange={(e) => handleFieldChange('custom_timepoints', e.target.value)}
                  placeholder="T0, 2W, 1M, 2M, 4M, etc."
                  size="small"
                  helperText="Define your own schedule"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="How many samples per timepoint?"
                  type="number"
                  fullWidth
                  value={formData.samples_per_timepoint || ''}
                  onChange={(e) => handleFieldChange('samples_per_timepoint', e.target.value)}
                  placeholder="3"
                  size="small"
                  helperText="Usually 3-5 samples for statistics"
                  inputProps={{ min: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pull_extra_samples || false}
                      onChange={(e) => handleFieldChange('pull_extra_samples', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Pull extra samples for unexpected testing
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Package Testing Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              Package & Product Setup
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="What package to test?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.package_configuration || ''}
                  onChange={(e) => handleFieldChange('package_configuration', e.target.value)}
                  placeholder="Final market package as sold; Primary container only (just bottle/tube); Bulk storage in large containers; Multiple package sizes; Worst case package (most permeable)"
                  size="small"
                  helperText="Type of packaging for testing"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Package details"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.package_details || ''}
                  onChange={(e) => handleFieldChange('package_details', e.target.value)}
                  placeholder="30ml amber glass bottle, 50g PE tube, 100ml PP jar"
                  size="small"
                  helperText="Specific container type and size"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Package orientation"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.package_orientation || ''}
                  onChange={(e) => handleFieldChange('package_orientation', e.target.value)}
                  placeholder="Upright in normal position; Inverted upside down; On side horizontal; Various positions with mixed orientations"
                  size="small"
                  helperText="How to position containers during storage"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.headspace_analysis || false}
                      onChange={(e) => handleFieldChange('headspace_analysis', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Monitor headspace/air in containers
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* What to Test Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3
            }}>
              What to Test - Parameters to monitor
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.appearance_check || false}
                      onChange={(e) => handleFieldChange('appearance_check', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Appearance (color, clarity, separation)
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.odor_check || false}
                      onChange={(e) => handleFieldChange('odor_check', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Odor/Fragrance stability
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.ph_monitoring || false}
                      onChange={(e) => handleFieldChange('ph_monitoring', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      pH measurement
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.viscosity_testing || false}
                      onChange={(e) => handleFieldChange('viscosity_testing', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Viscosity/Thickness changes
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.microbial_testing || false}
                      onChange={(e) => handleFieldChange('microbial_testing', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Microbial safety (bacteria/mold)
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.active_ingredient_assay || false}
                      onChange={(e) => handleFieldChange('active_ingredient_assay', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Active ingredient potency
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.preservative_effectiveness || false}
                      onChange={(e) => handleFieldChange('preservative_effectiveness', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Preservative effectiveness
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.package_integrity || false}
                      onChange={(e) => handleFieldChange('package_integrity', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Package integrity (leaks, cracks)
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Analysis & Reporting Section */}
        <Grid item xs={12}>
          <Box sx={{ 
            p: 4, 
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            mb: 3,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`
            }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 500, 
              color: theme.palette.primary.main, 
              mb: 3,
            }}>
              Analysis & Reporting
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="How to analyze trends?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.trend_analysis_method || ''}
                  onChange={(e) => handleFieldChange('trend_analysis_method', e.target.value)}
                  placeholder="Visual trending to graph the data; Linear regression for statistical line fitting; Statistical analysis with ANOVA/T-tests; Arrhenius modeling for predictive modeling; Simple comparison to limits"
                  size="small"
                  helperText="Method for analyzing stability data"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Regulatory compliance needed?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.regulatory_standard || ''}
                  onChange={(e) => handleFieldChange('regulatory_standard', e.target.value)}
                  placeholder="None for internal study only; FDA cosmetic guidelines; EU cosmetic regulation; ICH Q1A (pharmaceutical); ISO 22716 (cosmetic GMP); ASEAN cosmetic guidelines"
                  size="small"
                  helperText="Regulatory standards to follow"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="What happens if results fail?"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.failure_response_plan || ''}
                  onChange={(e) => handleFieldChange('failure_response_plan', e.target.value)}
                  placeholder="1. Investigate root cause, 2. Reformulate if needed, 3. Adjust shelf life claims, 4. Document corrective actions"
                  size="small"
                  helperText="Action plan for unexpected results"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Who gets the stability reports?"
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.report_distribution || ''}
                  onChange={(e) => handleFieldChange('report_distribution', e.target.value)}
                  placeholder="R&D Manager, QA Director, Regulatory Affairs, Marketing"
                  size="small"
                  helperText="People who need to see results"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(8px)',
                      borderRadius: 3,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                      },
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderWidth: 1.5
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.real_time_study || false}
                      onChange={(e) => handleFieldChange('real_time_study', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Real-time study (normal storage conditions)
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.accelerated_study || false}
                      onChange={(e) => handleFieldChange('accelerated_study', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      fontSize: '0.95rem'
                    }}>
                      Accelerated study (stress conditions)
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
});

StabilityStep.displayName = 'StabilityStep';

export default StabilityStep; 